import { Spinner } from '../components/feedback';
import { useState, type FormEvent } from 'react';
import type { EnrollmentOptions } from '@mcpanel/core';
import { usePoll } from '../hooks/usePoll';
import { ErrorAlert } from '../components/ui';
import { api } from '../api/client';
import { useApp, errorMessage } from '../app-context';
import { AuthField } from '../components/AuthFields';
import { AuthenticatorQr, RecoveryCodes } from '../components/Enrollment';
export function AccountSecurity() {
  const { me, notify, refreshMe } = useApp();
  const recovery = usePoll(api.recoveryStatus, 30000);
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [options, setOptions] = useState<EnrollmentOptions>();
  const [codes, setCodes] = useState<string[]>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const act = async (operation: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await operation();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
      setPassword('');
      setCode('');
    }
  };
  const change = (event: FormEvent) => {
    event.preventDefault();
    void act(async () => {
      await api.changePassword(password, code, newPassword);
      setNewPassword('');
      await refreshMe();
      notify('パスワードを変更しました。ほかのセッションは無効になりました。');
    });
  };
  return (
    <section className="card stack">
      <h2 data-search-id="account">パスワードと認証アプリ</h2>
      <p>
        ログイン ID: <code>{me?.user?.login ?? '開発用ログイン'}</code>（SFTP 名と共通）
      </p>
      <ErrorAlert error={recovery.error} />
      {recovery.data && <p>リカバリーコード: 残り {recovery.data.remaining} 個</p>}
      {recovery.data && recovery.data.remaining <= 2 && (
        <p className="alert warn">残りが少ないため、リカバリーコードの作り直しをおすすめします。</p>
      )}
      {error && <div className="alert danger">{error}</div>}
      {codes ? (
        <RecoveryCodes
          codes={codes}
          login={me?.user?.login ?? 'demo'}
          onDone={() => {
            setCodes(undefined);
            void refreshMe();
            void recovery.reload();
          }}
        />
      ) : options ? (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void act(async () => {
              try {
                setCodes((await api.totpVerify(confirmCode)).recoveryCodes);
              } finally {
                setOptions(undefined);
                setConfirmCode('');
              }
            });
          }}
        >
          <AuthenticatorQr options={options} />
          <AuthField label="新しい認証アプリのコード" value={confirmCode} onChange={setConfirmCode} code totpOnly />
          <button className="btn primary" disabled={busy}>
            {busy && <Spinner />}
            認証アプリを付け替える
          </button>
          <button className="btn" type="button" onClick={() => setOptions(undefined)}>
            キャンセル
          </button>
        </form>
      ) : (
        <form className="stack" onSubmit={change}>
          <AuthField label="今のパスワード" value={password} onChange={setPassword} password />
          <AuthField label="今のワンタイムコード / リカバリーコード" value={code} onChange={setCode} code />
          <AuthField label="新しいパスワード" value={newPassword} onChange={setNewPassword} password newPassword />
          <div className="row">
            <button className="btn primary" disabled={busy}>
              {busy && <Spinner />}
              パスワードを変更
            </button>
            <button
              className="btn"
              type="button"
              disabled={busy || !password || !code}
              onClick={() =>
                void act(async () => {
                  setOptions(await api.totpOptions(password, code));
                })
              }
            >
              {busy && <Spinner />}
              認証アプリを付け替える
            </button>
            <button
              className="btn"
              type="button"
              disabled={busy || !password || !/^[0-9]{6}$/.test(code)}
              onClick={() =>
                void act(async () => {
                  setCodes((await api.regenerateRecovery(password, code)).recoveryCodes);
                  await recovery.reload();
                })
              }
            >
              {busy && <Spinner />}
              リカバリーコードを作り直す
            </button>
          </div>
          <p className="small muted">
            作り直しには今のパスワードと認証アプリの6桁のコードが必要です。古いリカバリーコードはすべて使えなくなります。
          </p>
          <p className="small muted">
            付け替えでは今のパスワードとコードを確認します。付け替えると以前の認証アプリ・リカバリーコードは使えなくなります。
          </p>
        </form>
      )}
    </section>
  );
}
